import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/context/ConsentContext";
import { Cookie } from "lucide-react";

const COOKIES = [
  { name: "sni_consent_v1", provider: "Smart News Italia", purpose: "Memorizza le preferenze sui cookie", duration: "12 mesi", category: "Necessario", transfer: "Nessuno (locale)" },
  { name: "theme", provider: "Smart News Italia", purpose: "Ricorda il tema chiaro/scuro scelto", duration: "Persistente (locale)", category: "Necessario", transfer: "Nessuno (locale)" },
  { name: "Cookie e identificatori PostHog (es. ph_*)", provider: "PostHog", purpose: "Statistiche sull'utilizzo del sito", duration: "Fino a 12 mesi", category: "Statistiche", transfer: "Possibile extra-UE (SCC/DPF)" },
  { name: "Cookie di YouTube (es. VISITOR_INFO1_LIVE, YSC)", provider: "Google / YouTube", purpose: "Riproduzione dei video incorporati", duration: "Da sessione a 6 mesi", category: "Contenuti esterni", transfer: "Possibile extra-UE (SCC/DPF)" },
  { name: "Cloudflare Web Analytics (beacon.min.js)", provider: "Cloudflare", purpose: "Statistiche aggregate di traffico e prestazioni fornite dal CDN/hosting, senza cookie né identificatori individuali", duration: "Nessun cookie", category: "Tecnico", transfer: "Dati aggregati; possibile extra-UE (SCC)" },
  { name: "Cookie di Google AdSense (es. __gads, __eoi, IDE)", provider: "Google", purpose: "Pubblicità e misurazione degli annunci (caricato solo previo consenso 'Marketing')", duration: "Da sessione fino a 13 mesi", category: "Marketing", transfer: "Possibile extra-UE (SCC/DPF)" },
];

export default function CookiePolicy() {
  const { openPreferences } = useConsent();
  return (
    <div>
      <SEO title="Cookie Policy" description="Elenco dei cookie e dei servizi esterni realmente utilizzati da Smart News Italia, con finalità, durata, categoria ed eventuali trasferimenti." />
      <PageHero crumb="Cookie Policy" overline="Cookie e servizi esterni" title="Cookie Policy" subtitle="Quali cookie e servizi esterni utilizziamo, con quali finalità e per quanto tempo." icon={Cookie} />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 space-y-8 text-sm leading-relaxed text-muted-foreground">
        <p className="text-xs">Ultimo aggiornamento: 15 agosto 2026</p>

        <section className="space-y-2">
          <h2 className="font-serif text-xl font-bold text-foreground">Cosa sono i cookie</h2>
          <p>I cookie sono piccoli file di testo che i siti salvano sul dispositivo dell'utente. Utilizziamo <strong className="text-foreground">cookie tecnici necessari</strong> (che non richiedono consenso) e, <strong className="text-foreground">solo previo consenso</strong>, cookie e servizi di statistica e di terze parti. Prima del consenso restano attivi soltanto gli strumenti strettamente necessari: le statistiche (PostHog) e i contenuti di YouTube non vengono caricati.</p>
          <p>Il servizio di hosting/CDN (<strong className="text-foreground">Cloudflare</strong>) utilizza inoltre <strong className="text-foreground">Cloudflare Web Analytics</strong>, uno strumento di misurazione aggregata del traffico che <strong className="text-foreground">non imposta cookie</strong> e non memorizza informazioni sul dispositivo: per questo funziona anche prima del consenso, senza tracciare l'utente in modo individuale.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl font-bold text-foreground">Elenco dei cookie e dei servizi</h2>
          <div className="overflow-x-auto border border-border rounded-sm">
            <table className="w-full text-left text-xs sm:text-sm" data-testid="cookie-table">
              <thead className="bg-muted/60 text-foreground">
                <tr>
                  <th className="p-3 font-semibold">Nome</th>
                  <th className="p-3 font-semibold">Fornitore</th>
                  <th className="p-3 font-semibold">Finalità</th>
                  <th className="p-3 font-semibold">Durata</th>
                  <th className="p-3 font-semibold">Categoria</th>
                  <th className="p-3 font-semibold">Trasferimento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {COOKIES.map((c) => (
                  <tr key={c.name}>
                    <td className="p-3 text-foreground">{c.name}</td>
                    <td className="p-3">{c.provider}</td>
                    <td className="p-3">{c.purpose}</td>
                    <td className="p-3">{c.duration}</td>
                    <td className="p-3">{c.category}</td>
                    <td className="p-3">{c.transfer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs">Le email della newsletter sono inviate tramite Resend; questo servizio non imposta cookie sul sito e non utilizza pixel di tracciamento delle aperture o dei clic.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-xl font-bold text-foreground">Gestione del consenso</h2>
          <p>Puoi modificare o revocare le tue scelte in qualsiasi momento. La revoca del consenso disattiva gli strumenti non necessari.</p>
          <Button className="rounded-sm" onClick={openPreferences} data-testid="cookie-policy-manage-btn">
            Gestisci preferenze cookie
          </Button>
        </section>

        <p className="text-xs pt-4 border-t border-border">Per il trattamento dei dati personali consulta la <a className="text-primary hover:underline" href="/privacy-policy">Privacy Policy</a>.</p>
      </div>
    </div>
  );
}
